import React, { useState, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import clsx from 'clsx';
import { CloudUploadOutlined } from '@mui/icons-material';
import { useMountEffect } from '../../globals/hooks/use-mount-effect';
import FileUploader from '../file-loader';
import useStyles from './style';

const defaultMessage = 'Drop files to Upload';
const overrideEventDefaults = (event: DragEvent) => {
  event.preventDefault();
  event.stopPropagation();
};

const FileDrop = ({
  processAcceptedFile,
  acceptedFileTypes,
  customDropMessage: customMessage,
  maxFileSize,
  handleFileRejection,
}: {
  processAcceptedFile: Function;
  acceptedFileTypes: string[];
  customDropMessage: string;
  maxFileSize: number;
  handleFileRejection: Function;
}) => {
  const [dragging, setDragging] = useState(false);
  const container: React.RefObject<any> | null = useRef();
  const classes = useStyles();
  const checkIfDraggingOver = (event: DragEvent) =>
    container.current && container.current.contains(event.target);

  const handleDrag = (event: DragEvent) => {
    overrideEventDefaults(event);
    if (checkIfDraggingOver(event)) setDragging(true);
    else setDragging(false);
  };

  const acceptedFileType = (file: File) =>
    acceptedFileTypes.some((fileType: string) => file.name.includes(fileType));

  const acceptFileSize = (file: File) => file.size < maxFileSize;

  const processFile = (file: File) => {
    setDragging(false);
    if (acceptedFileType(file) && acceptFileSize(file)) processAcceptedFile(file);
    else handleFileRejection(file);
  };
  const handleDrop = (event: DragEvent) => {
    overrideEventDefaults(event);
    const file: File | null = event?.dataTransfer?.files[0] || null;
    if (checkIfDraggingOver(event) && file) processFile(file);
  };

  useMountEffect(() => {
    window.addEventListener('dragover', handleDrag);
    window.addEventListener('drop', handleDrop);
    return () => {
      window.removeEventListener('dragover', handleDrag);
      window.removeEventListener('drop', handleDrop);
    };
  });

  return (
    <div
      ref={container}
      className={clsx(classes.dropWindow, {
        [classes.dragging]: dragging,
        [classes.notDragging]: !dragging,
      })}
    >
      <Box pt={2}>
        <Typography className={classes.message} variant="h6">
          {customMessage || defaultMessage}
        </Typography>
      </Box>
      <Box p={2}>
        <CloudUploadOutlined className={classes.icon} />
      </Box>
      <Box pb={2}>
        <FileUploader handleFile={processFile} text="or click to select" />
      </Box>
    </div>
  );
};

export default FileDrop;
